/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Fragment, useState, useEffect } from "react";
import propTypes from "prop-types";
import { simpleFetch } from "../../../restWrapper";
import UpgradeNoticeCard from "./UpgradeNoticeCard";

const CustomEmailInput = ({
  domain,
  onChange,
  value,
  label = "Email Address",
  disabled = false,
  ...props
}) => {
  const [emailName, setEmailName] = useState(value ? value.split("@")[0] : "");

  // Sync internal state with prop value
  useEffect(() => {
    setEmailName(value ? value.split("@")[0] : "");
  }, [value]);

  const handleEmailNameChange = (e) => {
    const newEmailName = e.target.value;
    setEmailName(newEmailName);

    if (onChange) {
      onChange({
        target: {
          name: e.target.name,
          value: newEmailName,
        },
      });
    }
  };

  return (
    <div>
      <label className="OrgCustomDomain-email-input-label">{label}</label>
      <div className="OrgCustomDomain-email-input-container">
        <input
          type="text"
          className="OrgCustomDomain-email-name-input"
          value={emailName}
          onChange={handleEmailNameChange}
          placeholder="username"
          disabled={disabled}
          {...props}
        />
        <div className="OrgCustomDomain-email-domain-part">
          <span>@</span>
          <span>{domain}</span>
        </div>
      </div>
    </div>
  );
};

CustomEmailInput.propTypes = {
  domain: propTypes.string,
  onChange: propTypes.func,
  value: propTypes.string,
  label: propTypes.string,
  disabled: propTypes.bool,
};

const CustomDomainInput = ({
  onChange,
  value,
  label = "Email Address",
  disabled = false,
  ...props
}) => {
  const [domainName, setDomainName] = useState(value ? value : "");

  // Sync internal state with prop value
  useEffect(() => {
    setDomainName(value || "");
  }, [value]);

  const handleDomainNameChange = (e) => {
    const newDomainName = e.target.value;
    setDomainName(newDomainName);

    if (onChange) {
      onChange({
        target: {
          name: e.target.name,
          value: newDomainName,
        },
      });
    }
  };

  return (
    <div>
      <label className="OrgCustomDomain-domain-input-label">{label}</label>
      <div className="OrgCustomDomain-domain-input-container">
        <input
          type="text"
          className="OrgCustomDomain-domain-name-input"
          value={domainName}
          onChange={handleDomainNameChange}
          placeholder="example.com"
          disabled={disabled}
          {...props}
        />
      </div>
    </div>
  );
};

CustomDomainInput.propTypes = {
  onChange: propTypes.func,
  value: propTypes.string,
  label: propTypes.string,
  disabled: propTypes.bool,
};

export const RenderCustomDomain = ({ state, func, languageObj: _textObj }) => {
  const textObj = _textObj || {};
  const t = (key, fallback) => textObj[key] || fallback;
  if (!state || !func) return null;

  if (state.loading) {
    return (
      <div className="OrgInfo-emptyPage">
        <div className="OrgInfo-center">
          <div className="OrgInfo-btm-load"></div>
        </div>
      </div>
    );
  }

  const organizationObj = state.organizationObj || {};
  
  const currentPlan = organizationObj.currentPlan
    ? organizationObj.currentPlan.toLowerCase()
    : "";

  if (currentPlan !== "premium") {
    return (
      <div className="OrgCustomDomain OrgInfo-contentContainer">
        <div className="OrgInfo-contentHeader">
          <div className="OrgInfo-info">{t("info", "Custom Domain Setup")}</div>
        </div>
        <div className="OrgInfo-content flex column">
          <UpgradeNoticeCard
            heading={t("upgradeHeading", "Enable Custom Domain")}
            body={t("upgradeBody", "Custom domains and branded email are available on the Premium plan. Upgrade to connect your organisation's domain.")}
            onUpgradeClick={func.redirectToOrgBilling}
          />
        </div>
        <div className="OrgInfo-footer" />
      </div>
    );
  }

  const customDomainObj = organizationObj.domain || {};
  const domain = customDomainObj.domain || "";
  const tokens = customDomainObj.tokens || [];
  const email = customDomainObj.email || "";
  const primaryDomain = customDomainObj.emailDomain || "";
  const [changed, setChanged] = useState(false);
  // Primary domain is the main domain (e.g., edwardlai.com)
  const [customPrimaryDomain, setCustomPrimaryDomain] = useState(primaryDomain || "");
  // Payment subdomain is optional (e.g., payment)
  const [paymentSubdomain, setPaymentSubdomain] = useState("");
  // If existing domain is a subdomain of primaryDomain, extract it
  const [customEmail, setCustomEmail] = useState(email ? email.split('@')[0] : "");
  // Start in edit mode if there's no existing domain
  const [editMode, setEditMode] = useState(!primaryDomain);
  const [currentStep, setCurrentStep] = useState(customPrimaryDomain ? 2 : 1);
  const [verifyStatus, setVerifyStatus] = useState({
    domain: {
      status: false,
    },
    email: {
      status: false,
    },
  });
  const [forceUpdate, setForceUpdate] = useState(false);
  
  // Initialize payment subdomain if existing domain is a subdomain
  useEffect(() => {
    if (domain && primaryDomain && domain.endsWith(`.${primaryDomain}`)) {
      const subdomain = domain.replace(`.${primaryDomain}`, '');
      setPaymentSubdomain(subdomain);
    }
  }, [domain, primaryDomain]);

  const [isVerifying, setIsVerifying] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: '', showForceUpdate: false });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [domainEnabled, setDomainEnabled] = useState(customDomainObj.enabled || false);
  const [useCustomEmail, setUseCustomEmail] = useState(customDomainObj.useCustomEmail || false);
  const [isToggling, setIsToggling] = useState(false);
  const [isLoadingVerification, setIsLoadingVerification] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const verifyDomain = () => {
    const paymentDomain = paymentSubdomain ? `${paymentSubdomain}.${customPrimaryDomain}` : customPrimaryDomain;
    if (paymentDomain) {
      setIsVerifying(true);
      simpleFetch("/organizations/domains/verify", "POST").then((res) => {
        setIsVerifying(false);
        setIsLoadingVerification(false); // Clear loading state when API completes
        if ([200, 204].includes(res.status)) {
          setVerifyStatus(res.body);
        }
      }).catch((error) => {
        // console.error('Error verifying domain:', error);
        setIsVerifying(false);
        setIsLoadingVerification(false); // Clear loading state on error too
      });
    }
  };

  const toggleEditMode = () => {
    setEditMode(!editMode);
  };

  const handleForceUpdate = () => {
    setForceUpdate(true);
    setToast({ visible: false, message: '', showForceUpdate: false });
    // Retry saving with force update flag
    saveDomainWithForceUpdate();
  };

  const handleErrorMessage = (errorData) => {
    const paymentDomain = paymentSubdomain ? `${paymentSubdomain}.${customPrimaryDomain}` : customPrimaryDomain;
    let errorMessage = 'Unknown error';
    
    
    if (typeof errorData === 'string') {
      errorMessage = errorData;
    } else if (errorData && typeof errorData === 'object') {
      // Check for specific error types
      if (errorData.error === 'Host has already been taken' || errorData.error_code === 'host_taken') {
        errorMessage = `The domain "${paymentDomain}" is already registered. If this is your domain, you can force update it.`;
        // Always show force update option for host_taken errors
        setTimeout(() => {
          setToast({ 
            visible: true, 
            message: errorMessage,
            showForceUpdate: true 
          });
        }, 100);
        return; // Don't show the regular error toast
      } else if (errorData.error) {
        errorMessage = errorData.error;
      } else {
        errorMessage = JSON.stringify(errorData);
      }
    }
    
    setToast({ visible: true, message: `Failed to save domain: ${errorMessage}`, showForceUpdate: false });
    setTimeout(() => setToast({ visible: false, message: '', showForceUpdate: false }), 6000);
  };

  const saveDomainWithForceUpdate = () => {
    if (customPrimaryDomain) {
      setIsSaving(true);
      // Extract username part from email (remove @domain if present)
      const emailUsername = customEmail.includes('@') ? customEmail.split('@')[0] : customEmail;
      // Payment domain is either subdomain.primaryDomain or just primaryDomain
      const paymentDomain = paymentSubdomain ? `${paymentSubdomain}.${customPrimaryDomain}` : customPrimaryDomain;
      
      const payload = {
        domain: paymentDomain,  // This is the payment page domain
        email: emailUsername,
        emailDomain: customPrimaryDomain,  // Always use primary domain for emails
        forceUpdate: true  // Force update to true
      };
      simpleFetch("/organizations/domains/new", "POST", payload).then((res) => {
        if ([200, 204].includes(res.status)) {
          setIsSaving(false);
          setEditMode(false);
          setChanged(false);
          setForceUpdate(false); // Reset force update flag
          setCurrentStep(2); // Move to DNS setup step
          if (typeof func?.loadOrganizationData === 'function') {
            func.loadOrganizationData(); // Refresh organizationObj so freshly-generated DKIM tokens render in Step 2
          }
          setToast({ visible: true, message: 'Domain settings saved! Now let\'s set up DNS records.', showForceUpdate: false });
          setTimeout(() => setToast({ visible: false, message: '', showForceUpdate: false }), 4000);
        } else {
          setIsSaving(false);
          // Handle error
          // console.error('Failed to save domain. Full response:', res);
          // console.error('Response status:', res.status);
          // console.error('Response body:', res.body);

          // Handle different response types
          if (res.body instanceof ReadableStream || (res.body && res.body.locked !== undefined)) {
            // If ReadableStream is locked, it means restWrapper couldn't parse it
            // This usually happens with 500 errors or malformed JSON responses
            handleErrorMessage(`Server error (${res.status}). Please check the backend logs for details.`);
          } else {
            handleErrorMessage(res.body);
          }
        }
      }).catch((error) => {
        setIsSaving(false);
        // console.error('Error saving domain:', error);
        setToast({ visible: true, message: 'Error saving domain. Please try again.', showForceUpdate: false });
        setTimeout(() => setToast({ visible: false, message: '', showForceUpdate: false }), 6000);
      });
    }
  };

  const saveDomain = () => {
    if (customPrimaryDomain) {
      setIsSaving(true);
      // Extract username part from email (remove @domain if present)
      const emailUsername = customEmail.includes('@') ? customEmail.split('@')[0] : customEmail;
      // Payment domain is either subdomain.primaryDomain or just primaryDomain
      const paymentDomain = paymentSubdomain ? `${paymentSubdomain}.${customPrimaryDomain}` : customPrimaryDomain;
      
      const payload = {
        domain: paymentDomain,  // This is the payment page domain
        email: emailUsername,
        emailDomain: customPrimaryDomain,  // Always use primary domain for emails
        forceUpdate: forceUpdate  // Include force update flag
      };
      simpleFetch("/organizations/domains/new", "POST", payload).then((res) => {
        if ([200, 204].includes(res.status)) {
          setIsSaving(false);
          setEditMode(false);
          setChanged(false);
          setForceUpdate(false); // Reset force update flag
          setCurrentStep(2); // Move to DNS setup step
          if (typeof func?.loadOrganizationData === 'function') {
            func.loadOrganizationData(); // Refresh organizationObj so freshly-generated DKIM tokens render in Step 2
          }
          setToast({ visible: true, message: 'Domain settings saved! Now let\'s set up DNS records.', showForceUpdate: false });
          setTimeout(() => setToast({ visible: false, message: '', showForceUpdate: false }), 4000);
        } else {
          setIsSaving(false);
          // Handle error
          // console.error('Failed to save domain. Full response:', res);
          // console.error('Response status:', res.status);
          // console.error('Response body:', res.body);
          
          // Handle different response types
          if (res.body instanceof ReadableStream || (res.body && res.body.locked !== undefined)) {
            // If ReadableStream is locked, it means restWrapper couldn't parse it
            // This usually happens with 500 errors or malformed JSON responses
            handleErrorMessage(`Server error (${res.status}). Please check the backend logs for details.`);
          } else {
            handleErrorMessage(res.body);
          }
        }
      }).catch((error) => {
        setIsSaving(false);
        // console.error('Error saving domain:', error);
        setToast({ visible: true, message: 'Error saving domain. Please try again.', showForceUpdate: false });
        setTimeout(() => setToast({ visible: false, message: '', showForceUpdate: false }), 6000);
      });
    }
  };

  const cancelEdit = () => {
    setChanged(false);
    
    if (primaryDomain) {
      // Existing domain: restore original values and exit edit mode
      setCustomPrimaryDomain(primaryDomain);
      setPaymentSubdomain("");
      if (domain && primaryDomain && domain.endsWith(`.${primaryDomain}`)) {
        const subdomain = domain.replace(`.${primaryDomain}`, '');
        setPaymentSubdomain(subdomain);
      }
      setCustomEmail(email ? email.split('@')[0] : "");
      setEditMode(false);
    } else {
      // New domain setup: clear all fields and stay in edit mode
      setCustomPrimaryDomain("");
      setPaymentSubdomain("");
      setCustomEmail("");
    }
  };

  // Verify domain when moving to step 3 or when domain configuration is loaded
  useEffect(() => {
    if (currentStep === 3 && customPrimaryDomain) {
      setIsLoadingVerification(true);
      verifyDomain();
    }
  }, [currentStep]); // Only depend on currentStep to avoid multiple calls

  const clickToCopy = (text) => () => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setToast({ visible: true, message: t("copied", "Copied to clipboard!") });
        setTimeout(() => {
          setToast({ visible: false, message: '' });
        }, 2000);
      })
      .catch((err) => {
        setToast({ visible: true, message: 'Failed to copy text' });
        setTimeout(() => {
          setToast({ visible: false, message: '' });
        }, 3000);
        // console.error("Failed to copy text: ", err);
      });
  };

  const deleteDomain = async () => {
    setIsDeleting(true);
    try {
      const response = await simpleFetch("/organizations/domains/delete", "DELETE");
      if ([200, 204].includes(response.status)) {
        setToast({ visible: true, message: 'Domain configuration deleted successfully', showForceUpdate: false });
        setShowDeleteConfirm(false);
        // Reset all domain-related state
        setCustomPrimaryDomain("");
        setPaymentSubdomain("");
        setCustomEmail("");
        setEditMode(true);
        setCurrentStep(1);
        setTimeout(() => {
          setToast({ visible: false, message: '', showForceUpdate: false });
          // Reload the page to refresh organization data
          window.location.reload();
        }, 2000);
      } else {
        setToast({ visible: true, message: 'Failed to delete domain configuration', showForceUpdate: false });
        setTimeout(() => setToast({ visible: false, message: '', showForceUpdate: false }), 4000);
      }
    } catch (error) {
      // console.error('Error deleting domain:', error);
      setToast({ visible: true, message: 'Error deleting domain configuration', showForceUpdate: false });
      setTimeout(() => setToast({ visible: false, message: '', showForceUpdate: false }), 4000);
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleDomain = async () => {
    setIsToggling(true);
    try {
      const response = await simpleFetch("/organizations/domains/domain/toggle", "POST");
      if ([200, 204].includes(response.status)) {
        setDomainEnabled(!domainEnabled);
        setToast({ visible: true, message: `Custom domain ${!domainEnabled ? 'enabled' : 'disabled'} successfully`, showForceUpdate: false });
        setTimeout(() => setToast({ visible: false, message: '', showForceUpdate: false }), 3000);
      } else {
        setToast({ visible: true, message: 'Failed to toggle custom domain', showForceUpdate: false });
        setTimeout(() => setToast({ visible: false, message: '', showForceUpdate: false }), 4000);
      }
    } catch (error) {
      // console.error('Error toggling domain:', error);
      setToast({ visible: true, message: 'Error toggling custom domain', showForceUpdate: false });
      setTimeout(() => setToast({ visible: false, message: '', showForceUpdate: false }), 4000);
    } finally {
      setIsToggling(false);
    }
  };

  const toggleEmail = async () => {
    setIsToggling(true);
    try {
      const response = await simpleFetch("/organizations/domains/email/toggle", "POST");
      if ([200, 204].includes(response.status)) {
        setUseCustomEmail(!useCustomEmail);
        setToast({ visible: true, message: `Custom email ${!useCustomEmail ? 'enabled' : 'disabled'} successfully`, showForceUpdate: false });
        setTimeout(() => setToast({ visible: false, message: '', showForceUpdate: false }), 3000);
      } else {
        setToast({ visible: true, message: 'Failed to toggle custom email', showForceUpdate: false });
        setTimeout(() => setToast({ visible: false, message: '', showForceUpdate: false }), 4000);
      }
    } catch (error) {
      // console.error('Error toggling email:', error);
      setToast({ visible: true, message: 'Error toggling custom email', showForceUpdate: false });
      setTimeout(() => setToast({ visible: false, message: '', showForceUpdate: false }), 4000);
    } finally {
      setIsToggling(false);
    }
  };

  const paymentDomain = paymentSubdomain ? `${paymentSubdomain}.${customPrimaryDomain}` : customPrimaryDomain;
  
  const renderStepIndicator = () => (
    <div className="OrgCustomDomain-steps">
      <div className={`OrgCustomDomain-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
        <div className="OrgCustomDomain-step-number">1</div>
        <div className="OrgCustomDomain-step-text">{(textObj.steps && textObj.steps.step1) || "Domain Setup"}</div>
      </div>
      <div className="OrgCustomDomain-step-divider"></div>
      <div className={`OrgCustomDomain-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
        <div className="OrgCustomDomain-step-number">2</div>
        <div className="OrgCustomDomain-step-text">{(textObj.steps && textObj.steps.step2) || "DNS Configuration"}</div>
      </div>
      <div className="OrgCustomDomain-step-divider"></div>
      <div className={`OrgCustomDomain-step ${currentStep >= 3 ? 'active' : ''}`}>
        <div className="OrgCustomDomain-step-number">3</div>
        <div className="OrgCustomDomain-step-text">{(textObj.steps && textObj.steps.step3) || "Verification"}</div>
      </div>
    </div>
  );

  const renderStep1 = () => {
    const paymentDomain = paymentSubdomain ? `${paymentSubdomain}.${customPrimaryDomain}` : customPrimaryDomain;
    
    return (
      <div className="OrgCustomDomain-step-content">
        <div className="OrgCustomDomain-step-header">
          <h3>{t("setupHeading", "Set up your custom domain")}</h3>
          <p>{t("setupDesc", "Configure your primary domain for emails and optionally set up a subdomain for your payment page.")}</p>
        </div>
        
        <div className="OrgCustomDomain-form-section">
          <CustomDomainInput
            onChange={(e) => {
              setCustomPrimaryDomain(e.target.value);
              setChanged(true);
            }}
            value={customPrimaryDomain}
            label={t("primaryDomain", "Primary Domain")}
            disabled={(!editMode && !!primaryDomain) || isSaving}
            required
          />
          <div className="OrgCustomDomain-domain-help">
            <small>{t("primaryDomainHelp", "Your main domain (e.g., yourorganization.org). This will be used for sending emails.")}</small>
          </div>
        </div>

        <div className="OrgCustomDomain-form-section">
          <label className="OrgCustomDomain-domain-input-label">{t("paymentSubdomain", "Payment Page Subdomain (Optional)")}</label>
          <div className="OrgCustomDomain-subdomain-input-container">
            <input
              type="text"
              className="OrgCustomDomain-subdomain-input"
              value={paymentSubdomain}
              onChange={(e) => {
                setPaymentSubdomain(e.target.value);
                setChanged(true);
              }}
              placeholder="subdomain"
              disabled={(!editMode && !!primaryDomain) || isSaving}
            />
            <div className="OrgCustomDomain-subdomain-suffix">
              <span>.{customPrimaryDomain || "yourdomain.org"}</span>
            </div>
          </div>
          <div className="OrgCustomDomain-domain-help">
            <small>{t("paymentSubdomainHelp", "Optional: Create a subdomain for your payment page (e.g., payment.yourorganization.org)")}</small>
            <br />
            <small>{t("paymentSubdomainHelpBlank", "Leave blank to use your primary domain for both emails and payments.")}</small>
          </div>
        </div>

        <div className="OrgCustomDomain-form-section">
          <CustomEmailInput
            domain={customPrimaryDomain || "yourdomain.org"}
            onChange={(e) => {
              setCustomEmail(e.target.value);
              setChanged(true);
            }}
            value={customEmail}
            label={t("emailUsername", "Email Username")}
            disabled={(!editMode && !!primaryDomain) || isSaving}
            required
          />
          <div className="OrgCustomDomain-domain-help">
            <small>{t("emailUsernameHelp", "Choose a username for your custom email address")}</small>
          </div>
        </div>

        {paymentDomain && (
          <div className="OrgCustomDomain-preview-section">
            <h4>{t("preview", "Preview:")}</h4>
            <ul>
              <li><strong>{t("paymentPageUrl", "Payment page URL:")}</strong> {paymentDomain}</li>
              <li><strong>{t("emailAddress", "Email address:")}</strong> {customEmail || "username"}@{customPrimaryDomain}</li>
            </ul>
          </div>
        )}

        <div className="OrgCustomDomain-step-actions">
          {!customPrimaryDomain || editMode ? (
            <Fragment>
              <button 
                className="OrgCustomDomain-btn-primary"
                onClick={saveDomain}
                disabled={!customPrimaryDomain || !customEmail || !changed || isSaving}
              >
                {isSaving ? t("saving", "Saving...") : t("continueToDns", "Continue to DNS Setup")}
              </button>
              {customPrimaryDomain && (
                <button 
                  className="OrgCustomDomain-btn-secondary"
                  onClick={cancelEdit}
                  disabled={isSaving}
                >
                  {t("cancel", "Cancel")}
                </button>
              )}
            </Fragment>
          ) : (
            <Fragment>
              <button 
                className="OrgCustomDomain-btn-secondary"
                onClick={toggleEditMode}
              >
                {t("editSettings", "Edit Settings")}
              </button>
              <button 
                className="OrgCustomDomain-btn-primary"
                onClick={() => setCurrentStep(2)}
              >
                {t("viewDnsSetup", "View DNS Setup")}
              </button>
            </Fragment>
          )}
        </div>
      </div>
    );
  };

  const renderStep2 = () => {
    const currentPaymentDomain = paymentSubdomain ? `${paymentSubdomain}.${customPrimaryDomain}` : customPrimaryDomain;
    
    return (
      <div className="OrgCustomDomain-step-content">
        <div className="OrgCustomDomain-step-header">
          <h3>{t("configureDns", "Configure DNS Records")}</h3>
          <p>{t("configureDnsDesc", "Add these DNS records to your domain provider to complete the setup.")}</p>
        </div>

        <div className="OrgCustomDomain-dns-section">
          <div className="OrgCustomDomain-dns-type">
            {paymentSubdomain ? (
              <Fragment>
                <h4>{t("paymentSubdomainSetup", "Payment Page Subdomain Setup")}</h4>
                <p>Create these CNAME records for your payment subdomain ({currentPaymentDomain}):</p>
              </Fragment>
            ) : (
              <Fragment>
                <h4>{t("primaryDomainSetup", "Primary Domain Setup")}</h4>
                <p>{t("primaryDomainSetupDesc", "Since you're using your primary domain for payments, create these DNS records:")}</p>
              </Fragment>
            )}
          </div>

          <div className="OrgCustomDomain-dns-records">
            {paymentSubdomain ? (
              <div className="OrgCustomDomain-dns-record">
                <div className="OrgCustomDomain-dns-record-header">
                  <strong>CNAME Record</strong>
                </div>
                <div className="OrgCustomDomain-dns-record-row">
                  <div className="OrgCustomDomain-dns-field">
                    <label>{t("nameHost", "Name/Host:")}</label>
                    <div className="OrgCustomDomain-dns-value">
                      <code>{currentPaymentDomain}</code>
                      <button onClick={clickToCopy(currentPaymentDomain)} className="OrgCustomDomain-copy-btn">
                        Copy
                      </button>
                    </div>
                  </div>
                  <div className="OrgCustomDomain-dns-field">
                    <label>{t("pointsTo", "Points to:")}</label>
                    <div className="OrgCustomDomain-dns-value">
                      <code>in.saascustomdomains.com</code>
                      <button onClick={clickToCopy('in.saascustomdomains.com')} className="OrgCustomDomain-copy-btn">
                        Copy
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <Fragment>
                <div className="OrgCustomDomain-dns-record">
                  <div className="OrgCustomDomain-dns-record-header">
                    <strong>A Record #1</strong>
                  </div>
                  <div className="OrgCustomDomain-dns-record-row">
                    <div className="OrgCustomDomain-dns-field">
                      <label>{t("nameHost", "Name/Host:")}</label>
                      <div className="OrgCustomDomain-dns-value">
                        <code>{currentPaymentDomain}</code>
                        <button onClick={clickToCopy(currentPaymentDomain)} className="OrgCustomDomain-copy-btn">
                          Copy
                        </button>
                      </div>
                    </div>
                    <div className="OrgCustomDomain-dns-field">
                      <label>{t("pointsTo", "Points to:")}</label>
                      <div className="OrgCustomDomain-dns-value">
                        <code>99.83.186.151</code>
                        <button onClick={clickToCopy('99.83.186.151')} className="OrgCustomDomain-copy-btn">
                          Copy
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="OrgCustomDomain-dns-record">
                  <div className="OrgCustomDomain-dns-record-header">
                    <strong>A Record #2 (for high availability)</strong>
                  </div>
                  <div className="OrgCustomDomain-dns-record-row">
                    <div className="OrgCustomDomain-dns-field">
                      <label>{t("nameHost", "Name/Host:")}</label>
                      <div className="OrgCustomDomain-dns-value">
                        <code>{currentPaymentDomain}</code>
                        <button onClick={clickToCopy(currentPaymentDomain)} className="OrgCustomDomain-copy-btn">
                          Copy
                        </button>
                      </div>
                    </div>
                    <div className="OrgCustomDomain-dns-field">
                      <label>{t("pointsTo", "Points to:")}</label>
                      <div className="OrgCustomDomain-dns-value">
                        <code>75.2.96.173</code>
                        <button onClick={clickToCopy('75.2.96.173')} className="OrgCustomDomain-copy-btn">
                          Copy
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Fragment>
            )}

            <div className="OrgCustomDomain-dns-record">
              <div className="OrgCustomDomain-dns-record-header">
                <strong>CNAME Record (SSL certificate — DNS-01 challenge)</strong>
              </div>
              <div className="OrgCustomDomain-dns-record-row">
                <div className="OrgCustomDomain-dns-field">
                  <label>{t("nameHost", "Name/Host:")}</label>
                  <div className="OrgCustomDomain-dns-value">
                    <code>_acme-challenge.{currentPaymentDomain}</code>
                    <button onClick={clickToCopy(`_acme-challenge.${currentPaymentDomain}`)} className="OrgCustomDomain-copy-btn">
                      Copy
                    </button>
                  </div>
                </div>
                <div className="OrgCustomDomain-dns-field">
                  <label>{t("pointsTo", "Points to:")}</label>
                  <div className="OrgCustomDomain-dns-value">
                    <code>_acme-challenge.{currentPaymentDomain}.challenges.saascustomdomains.com</code>
                    <button onClick={clickToCopy(`_acme-challenge.${currentPaymentDomain}.challenges.saascustomdomains.com`)} className="OrgCustomDomain-copy-btn">
                      Copy
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {!paymentSubdomain && (
            <div className="OrgCustomDomain-domain-help">
              <small>{t("apexCnameNote", "Alternatively, if your DNS provider supports ALIAS/ANAME records (or CNAME flattening), you can point your root domain to in.saascustomdomains.com instead of the two A records.")}</small>
            </div>
          )}
        </div>

        {customEmail && (
          <div className="OrgCustomDomain-email-dns">
            <h4>{t("emailDnsRecords", "Email DNS Records (Primary Domain)")}</h4>
            <p>Add these DKIM records to your primary domain ({customPrimaryDomain}) to enable email sending:</p>
            
            {tokens.length > 0 ? (
              <div className="OrgCustomDomain-dns-records">
                {tokens.map((token, i) => (
                  <div key={token} className="OrgCustomDomain-dns-record">
                    <div className="OrgCustomDomain-dns-record-header">
                      <strong>CNAME Record for Email #{i + 1}</strong>
                    </div>
                    <div className="OrgCustomDomain-dns-record-row">
                      <div className="OrgCustomDomain-dns-field">
                        <label>{t("nameHost", "Name/Host:")}</label>
                        <div className="OrgCustomDomain-dns-value">
                          <code>{token}._domainkey.{customPrimaryDomain}</code>
                          <button onClick={clickToCopy(`${token}._domainkey.${customPrimaryDomain}`)} className="OrgCustomDomain-copy-btn">
                            Copy
                          </button>
                        </div>
                      </div>
                      <div className="OrgCustomDomain-dns-field">
                        <label>{t("pointsTo", "Points to:")}</label>
                        <div className="OrgCustomDomain-dns-value">
                          <code>{token}.dkim.amazonses.com</code>
                          <button onClick={clickToCopy(`${token}.dkim.amazonses.com`)} className="OrgCustomDomain-copy-btn">
                            Copy
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="OrgCustomDomain-dns-pending">
                <p className="OrgCustomDomain-dns-pending-message">
                  <strong>Note:</strong> {t("dkimSaveToGenerate", "DKIM records will be generated after you save your primary domain settings in Step 1.")}
                </p>
                <p>{t("dkimTypicalNote", "Typically, you'll need to add 3 CNAME records for DKIM email authentication.")}</p>
              </div>
            )}
          </div>
        )}

        <div className="OrgCustomDomain-dns-help">
          <div className="OrgCustomDomain-help-box">
            <h5>{t("needHelp", "Need help?")}</h5>
            <p>{t("needHelpDesc", "DNS changes can take up to 24 hours to take effect. Contact your domain provider if you need help adding these records.")}</p>
          </div>
        </div>

        <div className="OrgCustomDomain-step-actions">
          <button 
            className="OrgCustomDomain-btn-secondary"
            onClick={() => setCurrentStep(1)}
          >
            {t("backToSettings", "Back to Settings")}
          </button>
          <button 
            className="OrgCustomDomain-btn-primary"
            onClick={() => setCurrentStep(3)}
          >
            {t("checkVerification", "Check Verification")}
          </button>
        </div>
      </div>
    );
  };

  const renderLoadingSkeleton = () => {
    return (
      <div className="OrgCustomDomain-step-content">
        <div className="OrgCustomDomain-step-header-centered">
          <h1>{t("verificationHeading", "Verification Status & Configuration")}</h1>
          <p>{t("loadingVerification", "Loading your domain verification status...")}</p>
        </div>

        {/* Loading Overall Status */}
        <div className="OrgCustomDomain-loading-status-centered">
          <div className="OrgCustomDomain-loading-status-icon">
            <div className="OrgCustomDomain-loading-circle" style={{ width: '64px', height: '64px' }}></div>
          </div>
          <div className="OrgCustomDomain-loading-status-title OrgCustomDomain-loading-skeleton"></div>
          <div className="OrgCustomDomain-loading-status-description OrgCustomDomain-loading-skeleton"></div>
        </div>

        {/* Loading Unified Configuration & Verification Section */}
        <div className="OrgCustomDomain-verification-section-centered">
          <h2 className="OrgCustomDomain-section-title-large">{t("configVerification", "Configuration & Verification")}</h2>
          <div className="OrgCustomDomain-verification-grid">
            <div className="OrgCustomDomain-loading-unified-card">
              <div className="OrgCustomDomain-loading-verification-icon">
                <div className="OrgCustomDomain-loading-circle" style={{ width: '32px', height: '32px' }}></div>
              </div>
              <div className="OrgCustomDomain-loading-unified-header">
                <div className="OrgCustomDomain-loading-unified-title OrgCustomDomain-loading-skeleton"></div>
                <div className="OrgCustomDomain-loading-toggle-switch OrgCustomDomain-loading-skeleton"></div>
              </div>
              <div className="OrgCustomDomain-loading-verification-domain OrgCustomDomain-loading-skeleton"></div>
              <div className="OrgCustomDomain-loading-service-description OrgCustomDomain-loading-skeleton"></div>
              <div className="OrgCustomDomain-loading-verification-status OrgCustomDomain-loading-skeleton"></div>
              <div className="OrgCustomDomain-loading-verification-confirmed OrgCustomDomain-loading-skeleton"></div>
            </div>

            <div className="OrgCustomDomain-loading-unified-card">
              <div className="OrgCustomDomain-loading-verification-icon">
                <div className="OrgCustomDomain-loading-circle" style={{ width: '32px', height: '32px' }}></div>
              </div>
              <div className="OrgCustomDomain-loading-unified-header">
                <div className="OrgCustomDomain-loading-unified-title OrgCustomDomain-loading-skeleton"></div>
                <div className="OrgCustomDomain-loading-toggle-switch OrgCustomDomain-loading-skeleton"></div>
              </div>
              <div className="OrgCustomDomain-loading-verification-domain OrgCustomDomain-loading-skeleton"></div>
              <div className="OrgCustomDomain-loading-service-description OrgCustomDomain-loading-skeleton"></div>
              <div className="OrgCustomDomain-loading-verification-status OrgCustomDomain-loading-skeleton"></div>
              <div className="OrgCustomDomain-loading-verification-confirmed OrgCustomDomain-loading-skeleton"></div>
            </div>
          </div>
        </div>

        <div className="OrgCustomDomain-step-actions">
          <button className="OrgCustomDomain-btn-secondary" disabled>
            {t("backToDnsSetup", "Back to DNS Setup")}
          </button>
          <button className="OrgCustomDomain-btn-primary" disabled>
            {t("loading", "Loading...")}
          </button>
        </div>
      </div>
    );
  };

  const renderStep3 = () => {
    const currentPaymentDomain = paymentSubdomain ? `${paymentSubdomain}.${customPrimaryDomain}` : customPrimaryDomain;
    const allVerified = verifyStatus.domain.status && (!customEmail || verifyStatus.email.status);
    
    // Show loading skeleton if verification is loading
    if (isLoadingVerification) {
      return renderLoadingSkeleton();
    }
    
    return (
      <div className="OrgCustomDomain-step-content">
        <div className="OrgCustomDomain-step-header-centered">
          <h1>{t("verificationHeading", "Verification Status & Configuration")}</h1>
          <p>{t("verificationDesc", "Monitor your domain verification status and manage your custom domain settings.")}</p>
        </div>

        {/* Overall Status Card */}
        <div className="OrgCustomDomain-overall-status-centered">
          <div className={`OrgCustomDomain-status-icon-large ${allVerified ? 'success' : 'warning'}`}>
            {allVerified ? (
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#10B981" stroke="#10B981" strokeWidth="1.5"/>
                <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#F59E0B" stroke="#F59E0B" strokeWidth="1.5"/>
                <path d="M12 8V12M12 16H12.01" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
          <h2 className="OrgCustomDomain-status-title">{allVerified ? t("allOperational", "All Systems Operational") : t("setupInProgress", "Setup In Progress")}</h2>
          <p className="OrgCustomDomain-status-description">{allVerified
            ? t("allOperationalDesc", "Your custom domain is fully configured and ready to use.")
            : t("setupInProgressDesc", "Some verification steps are still pending. Please check the details below.")}</p>
        </div>

        {/* Unified Configuration & Verification Section */}
        <div className="OrgCustomDomain-verification-section-centered">
          <h2 className="OrgCustomDomain-section-title-large">{t("configVerification", "Configuration & Verification")}</h2>

          <div className="OrgCustomDomain-verification-grid">
            <div className="OrgCustomDomain-verification-detail-card">
              <div className="OrgCustomDomain-verification-detail-icon">
                {verifyStatus.domain.status ? (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" fill="#10B981"/>
                    <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" fill="#F59E0B"/>
                    <circle cx="12" cy="12" r="2" fill="white"/>
                  </svg>
                )}
              </div>
              <div className="OrgCustomDomain-unified-header">
                <h3>{t("customDomainLabel", "Custom Domain")}</h3>
                <label className="OrgCustomDomain-switch">
                  <input 
                    type="checkbox" 
                    checked={domainEnabled} 
                    onChange={toggleDomain}
                    disabled={isToggling || !verifyStatus.domain.status}
                  />
                  <span className="OrgCustomDomain-slider"></span>
                </label>
              </div>
              <p className="OrgCustomDomain-detail-domain">{currentPaymentDomain}</p>
              <p className="OrgCustomDomain-service-description">{t("enableCustomDomain", "Enable custom domain for your payment pages")}</p>
              <div className="OrgCustomDomain-detail-status">
                <span className="OrgCustomDomain-detail-status-label">{t("statusLabel", "Status:")}</span>
                <span className={`OrgCustomDomain-detail-status-value ${verifyStatus.domain.status ? 'verified' : 'pending'}`}>
                  {isVerifying ? t("checking", "Checking...") : verifyStatus.domain.status ? t("verified", "Verified") : t("pendingVerification", "Pending Verification")}
                </span>
              </div>
              {verifyStatus.domain.status && (
                <div className="OrgCustomDomain-detail-confirmed">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="7" r="6" fill="#10B981"/>
                    <path d="M4 7L6 9L10 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{t("dnsConfirmed", "DNS records confirmed")}</span>
                </div>
              )}
            </div>

            {customEmail && (
              <div className="OrgCustomDomain-verification-detail-card">
                <div className="OrgCustomDomain-verification-detail-icon">
                  {verifyStatus.email.status ? (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" fill="#10B981"/>
                      <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" fill="#F59E0B"/>
                      <circle cx="12" cy="12" r="2" fill="white"/>
                    </svg>
                  )}
                </div>
                <div className="OrgCustomDomain-unified-header">
                  <h3>{t("customEmailLabel", "Custom Email")}</h3>
                  <label className="OrgCustomDomain-switch">
                    <input 
                      type="checkbox" 
                      checked={useCustomEmail} 
                      onChange={toggleEmail}
                      disabled={isToggling || !verifyStatus.email.status}
                    />
                    <span className="OrgCustomDomain-slider"></span>
                  </label>
                </div>
                <p className="OrgCustomDomain-detail-domain">{customEmail}@{customPrimaryDomain}</p>
                <p className="OrgCustomDomain-service-description">{t("enableCustomEmail", "Send emails from your custom domain")}</p>
                <div className="OrgCustomDomain-detail-status">
                  <span className="OrgCustomDomain-detail-status-label">{t("statusLabel", "Status:")}</span>
                  <span className={`OrgCustomDomain-detail-status-value ${verifyStatus.email.status ? 'verified' : 'pending'}`}>
                    {isVerifying ? t("checking", "Checking...") : verifyStatus.email.status ? t("verified", "Verified") : t("pendingVerification", "Pending Verification")}
                  </span>
                </div>
                {verifyStatus.email.status && (
                  <div className="OrgCustomDomain-detail-confirmed">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <circle cx="7" cy="7" r="6" fill="#10B981"/>
                      <path d="M4 7L6 9L10 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>{t("dkimConfirmed", "DKIM records confirmed")}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Troubleshooting Section */}
        {(!verifyStatus.domain.status || (customEmail && !verifyStatus.email.status)) && (
          <div className="OrgCustomDomain-troubleshooting">
            <h4 className="OrgCustomDomain-section-title">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z" fill="#F59E0B"/>
              </svg>
              {t("troubleshootingGuide", "Troubleshooting Guide")}
            </h4>
            <div className="OrgCustomDomain-troubleshooting-grid">
              <div className="OrgCustomDomain-troubleshooting-item">
                <div className="OrgCustomDomain-troubleshooting-icon"><span role="img" aria-label="Clock">⏱️</span></div>
                <div className="OrgCustomDomain-troubleshooting-content">
                  <h5>{t("dnsPropagation", "DNS Propagation Time")}</h5>
                  <p>{t("dnsPropagationDesc", "DNS changes typically take 15-30 minutes but can take up to 24 hours to propagate globally.")}</p>
                </div>
              </div>
              <div className="OrgCustomDomain-troubleshooting-item">
                <div className="OrgCustomDomain-troubleshooting-icon"><span role="img" aria-label="Magnifying Glass">🔍</span></div>
                <div className="OrgCustomDomain-troubleshooting-content">
                  <h5>{t("verifyDnsRecords", "Verify DNS Records")}</h5>
                  <p>{t("verifyDnsRecordsDesc", "Use online DNS lookup tools to confirm your records are correctly configured.")}</p>
                </div>
              </div>
              <div className="OrgCustomDomain-troubleshooting-item">
                <div className="OrgCustomDomain-troubleshooting-icon"><span role="img" aria-label="Memo">📝</span></div>
                <div className="OrgCustomDomain-troubleshooting-content">
                  <h5>{t("checkForTypos", "Check for Typos")}</h5>
                  <p>{t("checkForTyposDesc", "Ensure all DNS values are copied exactly as shown, including dots and underscores.")}</p>
                </div>
              </div>
              <div className="OrgCustomDomain-troubleshooting-item">
                <div className="OrgCustomDomain-troubleshooting-icon"><span role="img" aria-label="Speech Balloon">💬</span></div>
                <div className="OrgCustomDomain-troubleshooting-content">
                  <h5>{t("contactSupport", "Contact Support")}</h5>
                  <p>{t("contactSupportDesc", "If issues persist, contact your domain provider or our support team for assistance.")}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="OrgCustomDomain-step-actions">
          <button 
            className="OrgCustomDomain-btn-secondary"
            onClick={() => setCurrentStep(2)}
          >
            {t("backToDnsSetup", "Back to DNS Setup")}
          </button>
          <button 
            className="OrgCustomDomain-btn-primary"
            onClick={verifyDomain}
            disabled={isVerifying}
          >
            {isVerifying ? t("checking", "Checking...") : t("checkAgain", "Check Again")}
          </button>
        </div>

        {/* Delete Domain Option */}
        {customPrimaryDomain && (
          <div className="OrgCustomDomain-danger-zone">
            <div className="OrgCustomDomain-danger-zone-header">
              <h5>{t("dangerZone", "Danger Zone")}</h5>
              <p>{t("dangerZoneDesc", "Once you delete a custom domain, all associated DNS records and email configurations will be removed.")}</p>
            </div>
            <button 
              className="OrgCustomDomain-btn-danger"
              onClick={() => setShowDeleteConfirm(true)}
            >
              {t("deleteCustomDomain", "Delete Custom Domain")}
            </button>
          </div>
        )}
      </div>
    );
  };


  return (
    <div className="OrgCustomDomain OrgInfo-contentContainer">
      {/* Delete Confirmaton Dialog */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999998
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            fontFamily: 'Arial, sans-serif'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#e74c3c' }}>{t("confirmDeleteHeading", "Confirm Domain Deletion")}</h3>
            <p style={{ marginBottom: '15px', lineHeight: '1.6', color: '#333' }}>
              {t("confirmDeleteDesc", "Are you sure you want to delete your custom domain configuration?")}
            </p>
            <div style={{
              backgroundColor: '#fff5f5',
              border: '1px solid #fdd',
              borderRadius: '4px',
              padding: '15px',
              marginBottom: '20px'
            }}>
              <strong style={{ color: '#e74c3c' }}>{t("thisActionWill", "This action will:")}</strong>
              <ul style={{ margin: '10px 0 0 20px', color: '#666' }}>
                <li>{t("deleteAction1", "Remove your custom domain from the SaaS Custom Domains portal")}</li>
                <li>{t("deleteAction2", "Delete all email sending configurations")}</li>
                <li>{t("deleteAction3", "Remove Apple Pay domain verification")}</li>
                <li>{t("deleteAction4", "Clear all DNS records associated with this domain")}</li>
              </ul>
            </div>
            <p style={{ marginBottom: '20px', fontWeight: 'bold', color: '#333' }}>
              Domain to delete: {paymentDomain}
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                style={{
                  backgroundColor: '#f8f9fa',
                  color: '#666',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                {t("cancel", "Cancel")}
              </button>
              <button
                style={{
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  opacity: isDeleting ? 0.6 : 1
                }}
                onClick={deleteDomain}
                disabled={isDeleting}
              >
                {isDeleting ? t("deleting", "Deleting...") : t("deleteDomain", "Delete Domain")}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Toast notification */}
      {toast.visible && (
        <div style={{ 
          position: 'fixed', 
          top: '20px', 
          right: '20px', 
          backgroundColor: '#ffffff',
          border: '3px solid #e74c3c',
          borderRadius: '8px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
          padding: '20px',
          maxWidth: '450px',
          minWidth: '300px',
          zIndex: 999999,
          fontSize: '14px',
          fontFamily: 'Arial, sans-serif',
          color: '#333'
        }}>
          <div style={{ 
            marginBottom: toast.showForceUpdate ? '15px' : '0',
            lineHeight: '1.6',
            fontWeight: '500'
          }}>
            <strong>{t("error", "Error:")}</strong><br />
            {toast.message || 'An error occurred'}
          </div>
          {toast.showForceUpdate && (
            <div style={{ 
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end'
            }}>
              <button 
                style={{
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 16px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
                onClick={handleForceUpdate}
              >
                {t("forceUpdateDomain", "Force Update Domain")}
              </button>
              <button 
                style={{
                  backgroundColor: '#f8f9fa',
                  color: '#666',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '10px 16px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
                onClick={() => setToast({ visible: false, message: '', showForceUpdate: false })}
              >
                {t("close", "Close")}
              </button>
            </div>
          )}
          {!toast.showForceUpdate && (
            <div style={{ 
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '10px'
            }}>
              <button 
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
                onClick={() => setToast({ visible: false, message: '', showForceUpdate: false })}
              >
                {t("close", "Close")}
              </button>
            </div>
          )}
        </div>
      )}
      
      <div className="OrgInfo-contentHeader">
        <div className="OrgInfo-info">{t("info", "Custom Domain Setup")}</div>
      </div>
      
      <div className="OrgInfo-content">
        <div className="OrgCustomDomain-wizard">
          {renderStepIndicator()}
          
          <div className="OrgCustomDomain-step-container">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
          </div>
        </div>
      </div>
      
      <div className="OrgInfo-footer" />
    </div>
  );
};

RenderCustomDomain.propTypes = {
  state: propTypes.object.isRequired,
  func: propTypes.object.isRequired,
  languageObj: propTypes.object,
};
